import React, { useRef } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { getToken } from "../../../utils/auth";
import {
  useGetSessionByIdQuery,
  usePublishSessionStatusChangeMutation,
} from "../../../services";
import { toast } from "../../../core/toast";

const SessionPreview: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { sessionid } = useParams<{ sessionid?: string }>();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const token = getToken();
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const {
    data: sessionData,
    isLoading: isFetching,
    isError: isSessionError,
  } = useGetSessionByIdQuery(sessionid!, { skip: !sessionid });
  const [publishSession, { isLoading: isPublishing }] =
    usePublishSessionStatusChangeMutation();
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  const themeBox = theme === "dark";

  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";
  const handlePublish = async () => {
    try {
      if (!sessionid) {
        toast.error("Session ID is missing.");
        return;
      }

      const currentStatus = sessionData?.result?.status;

      if (currentStatus === "PUBLISHED") {
        navigate(`${adminRoutes.sessionList}`);
        return;
      }

      await publishSession({ sessionid }).unwrap();
      navigate(`${adminRoutes.sessionList}`);
    } catch (err) {
      console.error("Failed to publish session:", err);
      toast.error("Failed to publish session.");
    }
  };


  const handleFullscreen = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if ((iframe as any).webkitRequestFullscreen) {
        // Safari fallback
        (iframe as any).webkitRequestFullscreen();
      } else if ((iframe as any).msRequestFullscreen) {
        // Edge/IE fallback
        (iframe as any).msRequestFullscreen();
      }
    }
  };
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
            }`}
        >
          Session Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            New SessionS
          </span>
        </div>
      </div>

      {/* Preview Card */}
      <div
        className={`${cardClass} rounded-lg p-0`}
        style={{ overflow: "hidden" }}
      >
        {/* Fullscreen Button Bar */}
        <div className={`flex justify-end p-2`}>
          <button
            className={`transition-opacity hover:opacity-80`}
            onClick={handleFullscreen}
          >
            <svg width="143" height="42" viewBox="0 0 143 42" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="Depth 7, Frame 6">
                <rect width="142.997" height="42" rx="10" fill="url(#paint0_radial_5290_1919)" fill-opacity="0.4" />
                <rect x="0.5" y="0.5" width="141.997" height="41" rx="9.5" stroke="url(#paint1_linear_5290_1919)" stroke-opacity="0.88" />
                <g id="Group">
                  <path id="Vector" d="M36.2741 33H19.7231C17.6699 33 16 31.3301 16 29.2769V12.7259C16 10.6699 17.6699 9 19.7231 9H36.2741C38.3273 9 39.9972 10.6699 39.9972 12.7231V29.2741C39.9972 31.3301 38.3273 33 36.2741 33ZM19.7231 10.7203C18.6182 10.7203 17.7175 11.621 17.7175 12.7259V29.2769C17.7175 30.3818 18.6182 31.2825 19.7231 31.2825H36.2741C37.379 31.2825 38.2797 30.3818 38.2797 29.2769V12.7259C38.2797 11.621 37.379 10.7203 36.2741 10.7203H19.7231Z" fill="#F3F3F6" />
                  <path id="Vector_2" d="M35.0243 19.7944C34.5516 19.7944 34.1656 19.4084 34.1656 18.9357V15.358C34.1656 15.042 33.9082 14.7846 33.5921 14.7846H29.7572C29.2844 14.7846 28.8984 14.3986 28.8984 13.9259C28.8984 13.4532 29.2844 13.0671 29.7572 13.0671H33.5921C34.8565 13.0671 35.883 14.0965 35.883 15.358V18.9357C35.883 19.4112 35.497 19.7944 35.0243 19.7944ZM26.4369 28.8154H22.5292C21.2649 28.8154 20.2383 27.786 20.2383 26.5245V22.7259C20.2383 22.2532 20.6243 21.8671 21.097 21.8671C21.5697 21.8671 21.9558 22.2532 21.9558 22.7259V26.5217C21.9558 26.8378 22.2131 27.0951 22.5292 27.0951H26.4369C26.9096 27.0951 27.2956 27.4811 27.2956 27.9539C27.2956 28.4266 26.9124 28.8154 26.4369 28.8154Z" fill="#C6D92C" />
                </g>
                <g id="Depth 8, Frame 1">
                  <path id="Full Screen" d="M53.0004 26V15.8182H59.5231V17.3643H54.8448V20.1286H59.0756V21.6747H54.8448V26H53.0004ZM65.5126 22.7884V18.3636H67.3123V26H65.5673V24.6428H65.4877C65.3154 25.0703 65.032 25.42 64.6376 25.6918C64.2465 25.9635 63.7643 26.0994 63.1909 26.0994C62.6904 26.0994 62.2479 25.9884 61.8635 25.7663C61.4823 25.541 61.184 25.2145 60.9686 24.7869C60.7531 24.3561 60.6454 23.8357 60.6454 23.2259V18.3636H62.4451V22.9474C62.4451 23.4313 62.5777 23.8158 62.8429 24.1009C63.108 24.3859 63.456 24.5284 63.8869 24.5284C64.152 24.5284 64.4089 24.4638 64.6575 24.3345C64.9061 24.2053 65.1099 24.013 65.269 23.7578C65.4314 23.4993 65.5126 23.1761 65.5126 22.7884ZM70.9627 15.8182V26H69.163V15.8182H70.9627ZM74.6131 15.8182V26H72.8134V15.8182H74.6131ZM85.5494 18.6172C85.503 18.183 85.3074 17.8449 84.9627 17.603C84.6213 17.361 84.1772 17.2401 83.6303 17.2401C83.2459 17.2401 82.9161 17.2981 82.641 17.4141C82.3659 17.5301 82.1554 17.6875 82.0096 17.8864C81.8638 18.0852 81.7892 18.3123 81.7859 18.5675C81.7859 18.7796 81.8339 18.9635 81.93 19.1193C82.0295 19.2751 82.1637 19.4077 82.3327 19.517C82.5018 19.6231 82.689 19.7126 82.8945 19.7855C83.1 19.8584 83.3072 19.9197 83.516 19.9695L84.4705 20.2081C84.855 20.2976 85.2246 20.4186 85.5792 20.571C85.9371 20.7235 86.257 20.9157 86.5387 21.1477C86.8237 21.3797 87.0491 21.6598 87.2148 21.9879C87.3806 22.3161 87.4634 22.7005 87.4634 23.1413C87.4634 23.7379 87.311 24.2633 87.006 24.7173C86.7011 25.1681 86.2603 25.5211 85.6836 25.7763C85.1102 26.0282 84.4158 26.1541 83.6005 26.1541C82.8084 26.1541 82.1206 26.0315 81.5373 25.7862C80.9573 25.541 80.5032 25.183 80.1751 24.7124C79.8503 24.2417 79.6746 23.6683 79.6481 22.9922H81.4627C81.4892 23.3468 81.5986 23.6418 81.7908 23.8771C81.9831 24.1125 82.2333 24.2881 82.5415 24.4041C82.8531 24.5201 83.2011 24.5781 83.5856 24.5781C83.9866 24.5781 84.338 24.5185 84.6396 24.3991C84.9445 24.2765 85.1831 24.1075 85.3555 23.892C85.5278 23.6733 85.6156 23.4181 85.619 23.1264C85.6156 22.8613 85.5378 22.6425 85.3853 22.4702C85.2328 22.2945 85.0191 22.1487 84.744 22.0327C84.4722 21.9134 84.154 21.8073 83.7894 21.7145L82.631 21.4162C81.7925 21.2008 81.1296 20.8743 80.6424 20.4368C80.1585 19.996 79.9165 19.411 79.9165 18.6818C79.9165 18.0819 80.079 17.5566 80.4038 17.1058C80.7319 16.6551 81.1777 16.3054 81.7411 16.0568C82.3046 15.8049 82.9426 15.679 83.6552 15.679C84.3777 15.679 85.0108 15.8049 85.5543 16.0568C86.1012 16.3054 86.5304 16.6518 86.842 17.0959C87.1535 17.5367 87.3143 18.0438 87.3242 18.6172H85.5494ZM92.3592 26.1491C91.5969 26.1491 90.9423 25.9818 90.3954 25.647C89.8519 25.3123 89.4326 24.8499 89.1376 24.2599C88.8459 23.6667 88.7001 22.9839 88.7001 22.2116C88.7001 21.4361 88.8493 20.7517 89.1475 20.1584C89.4458 19.5618 89.8668 19.0978 90.4103 18.7663C90.9572 18.4316 91.6035 18.2642 92.3493 18.2642C92.969 18.2642 93.5176 18.3786 93.9949 18.6072C94.4754 18.8326 94.8583 19.1525 95.1433 19.5668C95.4283 19.9777 95.5907 20.4583 95.6305 21.0085H93.9103C93.8407 20.6406 93.675 20.334 93.4132 20.0888C93.1547 19.8402 92.8083 19.7159 92.3741 19.7159C92.0062 19.7159 91.6831 19.8153 91.4047 20.0142C91.1262 20.2098 90.9092 20.4915 90.7534 20.8594C90.6009 21.2273 90.5247 21.6681 90.5247 22.1818C90.5247 22.7022 90.6009 23.1496 90.7534 23.5241C90.9058 23.8954 91.1196 24.1821 91.3947 24.3842C91.6731 24.5831 91.9996 24.6825 92.3741 24.6825C92.6393 24.6825 92.8762 24.6328 93.085 24.5334C93.2972 24.4306 93.4745 24.2831 93.617 24.0909C93.7595 23.8987 93.8573 23.665 93.9103 23.3899H95.6305C95.5874 23.9302 95.4283 24.4091 95.1532 24.8267C94.8781 25.241 94.5036 25.5658 94.0297 25.8011C93.5557 26.0331 92.9989 26.1491 92.3592 26.1491ZM97.1083 26V18.3636H98.8533V19.6364H98.9329C99.0721 19.1955 99.3107 18.8558 99.6488 18.6172C99.9902 18.3752 100.38 18.2543 100.817 18.2543C100.917 18.2543 101.028 18.2592 101.15 18.2692C101.276 18.2758 101.381 18.2874 101.463 18.304V19.9595C101.387 19.933 101.266 19.9098 101.1 19.8899C100.938 19.8667 100.781 19.8551 100.628 19.8551C100.3 19.8551 100.005 19.9264 99.7433 20.0689C99.4847 20.2081 99.2809 20.402 99.1317 20.6506C98.9826 20.8991 98.908 21.1858 98.908 21.5107V26H97.1083ZM105.725 26.1491C104.96 26.1491 104.298 25.9901 103.742 25.6719C103.188 25.3504 102.762 24.8963 102.464 24.3097C102.166 23.7197 102.017 23.0253 102.017 22.2266C102.017 21.4411 102.166 20.7517 102.464 20.1584C102.766 19.5618 103.186 19.0978 103.727 18.7663C104.267 18.4316 104.902 18.2642 105.631 18.2642C106.102 18.2642 106.546 18.3404 106.963 18.4929C107.384 18.642 107.755 18.8741 108.077 19.1889C108.402 19.5038 108.657 19.9048 108.843 20.392C109.028 20.8759 109.121 21.4527 109.121 22.1222V22.674H102.862V21.4609H107.396C107.392 21.1162 107.318 20.8097 107.172 20.5412C107.026 20.2694 106.822 20.0556 106.561 19.8999C106.302 19.7441 106 19.6662 105.656 19.6662C105.288 19.6662 104.965 19.7557 104.686 19.9347C104.408 20.1103 104.191 20.3423 104.035 20.6307C103.883 20.9157 103.805 21.2289 103.801 21.5703V22.6293C103.801 23.0734 103.883 23.4545 104.045 23.7727C104.207 24.0876 104.434 24.3295 104.726 24.4986C105.018 24.6643 105.359 24.7472 105.75 24.7472C106.012 24.7472 106.249 24.7107 106.461 24.6378C106.673 24.5616 106.857 24.4505 107.013 24.3047C107.169 24.1589 107.286 23.9782 107.366 23.7628L109.046 23.9517C108.94 24.3958 108.738 24.7836 108.44 25.1151C108.145 25.4432 107.767 25.6984 107.306 25.8807C106.846 26.0597 106.319 26.1491 105.725 26.1491ZM114.024 26.1491C113.259 26.1491 112.597 25.9901 112.04 25.6719C111.487 25.3504 111.061 24.8963 110.763 24.3097C110.464 23.7197 110.315 23.0253 110.315 22.2266C110.315 21.4411 110.464 20.7517 110.763 20.1584C111.064 19.5618 111.485 19.0978 112.026 18.7663C112.566 18.4316 113.201 18.2642 113.93 18.2642C114.4 18.2642 114.844 18.3404 115.262 18.4929C115.683 18.642 116.054 18.8741 116.376 19.1889C116.701 19.5038 116.956 19.9048 117.141 20.392C117.327 20.8759 117.42 21.4527 117.42 22.1222V22.674H111.161V21.4609H115.695C115.691 21.1162 115.617 20.8097 115.471 20.5412C115.325 20.2694 115.121 20.0556 114.859 19.8999C114.601 19.7441 114.299 19.6662 113.955 19.6662C113.587 19.6662 113.263 19.7557 112.985 19.9347C112.707 20.1103 112.49 20.3423 112.334 20.6307C112.181 20.9157 112.103 21.2289 112.1 21.5703V22.6293C112.1 23.0734 112.181 23.4545 112.344 23.7727C112.506 24.0876 112.733 24.3295 113.025 24.4986C113.317 24.6643 113.658 24.7472 114.049 24.7472C114.311 24.7472 114.548 24.7107 114.76 24.6378C114.972 24.5616 115.156 24.4505 115.312 24.3047C115.468 24.1589 115.585 23.9782 115.665 23.7628L117.345 23.9517C117.239 24.3958 117.037 24.7836 116.739 25.1151C116.444 25.4432 116.066 25.6984 115.605 25.8807C115.144 26.0597 114.617 26.1491 114.024 26.1491ZM120.742 21.5256V26H118.942V18.3636H120.662V19.6612H120.752C120.928 19.2337 121.208 18.8939 121.592 18.642C121.98 18.3902 122.459 18.2642 123.029 18.2642C123.556 18.2642 124.015 18.3769 124.406 18.6023C124.8 18.8277 125.105 19.1541 125.321 19.5817C125.54 20.0092 125.647 20.5279 125.644 21.1378V26H123.844V21.4162C123.844 20.9058 123.712 20.5064 123.447 20.218C123.185 19.9297 122.822 19.7855 122.358 19.7855C122.043 19.7855 121.763 19.8551 121.518 19.9943C121.276 20.1302 121.085 20.3274 120.946 20.5859C120.81 20.8445 120.742 21.1577 120.742 21.5256Z" fill="white" />
                </g>
              </g>
              <defs>
                <radialGradient id="paint0_radial_5290_1919" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(75.0381) rotate(90) scale(42 134.662)">
                  <stop stop-color="#C6D92C" />
                  <stop offset="1" stop-color="#535B10" />
                </radialGradient>
                <linearGradient id="paint1_linear_5290_1919" x1="0" y1="21" x2="142.997" y2="21" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#C6D92C" />
                  <stop offset="0.533654" stop-color="#EDF5C0" />
                  <stop offset="1" stop-color="#C6D92C" />
                </linearGradient>
              </defs>
            </svg>
          </button>
        </div>

        <div
          className={`w-full h-[420px] flex items-center justify-center ${theme === "dark" ? "bg-[#181818]" : "bg-gray-100"
            }`}
        >
          <iframe
            ref={iframeRef}
            src={`${import.meta.env.VITE_TV_APP_URL
              }/preview/${sessionid}/${token}`}
            title="GymStream TV Preview"
            className="w-full h-full border-0 rounded-none"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />





        </div>




        {/* Footer: Actions */}
        <div
          className={`flex items-center justify-center px-6 py-4 border-t ${themeBox ? "border-[#232323]" : "border-gray-200"
            }`}
        >
          <div className="flex items-center gap-4">
            <button
              className={`flex items-center gap-2 px-5 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed ${theme === "dark"
                ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              }}
              onClick={() => handlePublish()}
              disabled={isPublishing}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M15.5376 18.592C14.4312 19.5 12.7875 19.5 9.5 19.5C6.21252 19.5 4.56878 19.5 3.46243 18.592C3.25989 18.4258 3.07418 18.2401 2.90796 18.0376C2 16.9312 2 15.2875 2 12C2 8.71252 2 7.06878 2.90796 5.96243C3.07418 5.75989 3.25989 5.57418 3.46243 5.40796C4.56878 4.5 6.21252 4.5 9.5 4.5C12.7875 4.5 14.4312 4.5 15.5376 5.40796C15.7401 5.57418 15.9258 5.75989 16.092 5.96243C17 7.06878 17 8.71252 17 12C17 15.2875 17 16.9312 16.092 18.0376C15.9258 18.2401 15.7401 18.4258 15.5376 18.592Z"
                  stroke="#F3F3F6"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
                <path
                  d="M17 13V11L19.6 7.53333C19.8518 7.19759 20.247 7 20.6667 7C21.403 7 22 7.59695 22 8.33333V15.6667C22 16.403 21.403 17 20.6667 17C20.247 17 19.8518 16.8024 19.6 16.4667L17 13Z"
                  stroke="#F3F3F6"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
                <path
                  d="M9.5 13.5C10.3284 13.5 11 12.8284 11 12C11 11.1716 10.3284 10.5 9.5 10.5M9.5 13.5C8.67157 13.5 8 12.8284 8 12C8 11.1716 8.67157 10.5 9.5 10.5M9.5 13.5V10.5"
                  stroke="#C6D92C"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
              </svg>
              {isPublishing
                ? "Publishing..."
                : sessionData?.result?.status === "PUBLISHED"
                  ? "Published"
                  : "Publish"}
            </button>
            <button
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              onClick={() =>
                navigate(
                  `${adminRoutes.sessionSummaryBuilder.replace(
                    ":sessionid",
                    String(sessionid || ""),
                  )}${categoryId
                    ? `?category=${encodeURIComponent(categoryId)}`
                    : ""
                  }`,
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 6H15.5C17.9853 6 20 8.01472 20 10.5C20 12.9853 17.9853 15 15.5 15H4"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.99998 12C6.99998 12 4.00001 14.2095 4 15C3.99999 15.7906 7 18 7 18"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Previous
            </button>

            <button
              onClick={() => navigate(adminRoutes.sessionList)}
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9L9 14.9996M15 15L9 9.00039"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionPreview;
